import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type ScrollBlockAnimState = "hidden" | "visible" | "instant";

export const scrollBlockInViewConfig = {
  triggerOnce: false,
  threshold: [0, 0.06, 0.12, 0.18, 0.25] as number[],
  rootMargin: "5% 0px",
};

export const SCROLL_BLOCK_ENTER_RATIO = 0.18;
export const SCROLL_BLOCK_EXIT_RATIO = 0.06;
export const SCROLL_BLOCK_HIDE_DEBOUNCE_MS = 220;

type UseScrollDirectionOptions = {
  /** モバイルで touchmove からも方向を更新 */
  enableTouch?: boolean;
  /** スクロールイベントをスロットル（Trailer / Story 互換） */
  throttled?: boolean;
};

/** スクロール方向が変わったときだけ state を更新 */
export function useScrollDirection(options?: UseScrollDirectionOptions) {
  const { enableTouch = false, throttled = false } = options ?? {};
  const [scrollDirection, setScrollDirection] = useState<"down" | "up">("down");
  const scrollDirRef = useRef(scrollDirection);
  scrollDirRef.current = scrollDirection;
  const prevScrollY = useRef(0);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastScrollTimeRef = useRef(0);
  const onDirectionChangeRef = useRef<(() => void) | null>(null);

  const notifyDirectionChange = useCallback((newDirection: "down" | "up") => {
    if (newDirection === scrollDirRef.current) return;
    scrollDirRef.current = newDirection;
    setScrollDirection(newDirection);
    onDirectionChangeRef.current?.();
  }, []);

  useEffect(() => {
    prevScrollY.current = window.scrollY;
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const throttleTime = throttled ? (isMobile ? 16 : 50) : 0;

    const handleScroll = () => {
      const run = () => {
        const currentScrollY = window.scrollY;
        const diff = currentScrollY - prevScrollY.current;
        prevScrollY.current = currentScrollY;
        if (Math.abs(diff) <= 2) return;
        notifyDirectionChange(diff > 0 ? "down" : "up");
      };

      if (!throttled) {
        run();
        return;
      }

      const now = Date.now();
      if (now - lastScrollTimeRef.current < throttleTime) {
        if (scrollTimerRef.current === null) {
          scrollTimerRef.current = setTimeout(() => {
            lastScrollTimeRef.current = Date.now();
            scrollTimerRef.current = null;
            handleScroll();
          }, throttleTime);
        }
        return;
      }

      lastScrollTimeRef.current = now;
      run();
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    let touchStartY = 0;
    const handleTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY;
    };
    const handleTouchMove = (e: TouchEvent) => {
      const touchY = e.touches[0].clientY;
      const diff = touchStartY - touchY;
      if (Math.abs(diff) > 10) {
        notifyDirectionChange(diff > 0 ? "down" : "up");
        touchStartY = touchY;
      }
    };

    if (enableTouch && isMobile) {
      window.addEventListener("touchstart", handleTouchStart, {
        passive: true,
      });
      window.addEventListener("touchmove", handleTouchMove, { passive: true });
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (enableTouch && isMobile) {
        window.removeEventListener("touchstart", handleTouchStart);
        window.removeEventListener("touchmove", handleTouchMove);
      }
      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current);
      }
    };
  }, [enableTouch, notifyDirectionChange, throttled]);

  const registerDirectionChange = useCallback((handler: () => void) => {
    onDirectionChangeRef.current = handler;
    return () => {
      if (onDirectionChangeRef.current === handler) {
        onDirectionChangeRef.current = null;
      }
    };
  }, []);

  return { scrollDirection, scrollDirRef, registerDirectionChange };
}

export type ScrollBlockAnimControllerOptions<K extends string> = {
  /** false のキーは intersection / 表示更新をスキップ（Trailer 動画非表示時など） */
  isKeyEnabled?: (key: K) => boolean;
  /** hidden → 表示へ遷移した直後（Trailer の videoReplay など） */
  onBecameVisible?: (key: K, prev: ScrollBlockAnimState) => void;
  /**
   * true のキーは、わずかに見えているだけ（enter 未満）のときは hidden に戻さない。
   * 境目での二重表示を防ぎつつ、完全に画面外になったら再アニメ可能。
   */
  suppressBoundaryHide?: (key: K) => boolean;
};

export function useScrollBlockAnimController<K extends string>(
  keys: readonly K[],
  scrollDirRef: React.MutableRefObject<"down" | "up">,
  options?: ScrollBlockAnimControllerOptions<K>,
) {
  const { isKeyEnabled, onBecameVisible, suppressBoundaryHide } = options ?? {};
  const isEnabled = useCallback(
    (key: K) => isKeyEnabled?.(key) ?? true,
    [isKeyEnabled],
  );
  const lastRatioRef = useRef(
    Object.fromEntries(keys.map((k) => [k, 0])) as Record<K, number>,
  );
  const lastEntryRef = useRef(
    Object.fromEntries(keys.map((k) => [k, null])) as Record<
      K,
      IntersectionObserverEntry | null
    >,
  );
  const shouldSkipBoundaryHide = useCallback(
    (key: K) => {
      if (!suppressBoundaryHide?.(key)) return false;
      const ratio = lastRatioRef.current[key];
      if (!(ratio > 0 && ratio < SCROLL_BLOCK_ENTER_RATIO)) return false;
      const entry = lastEntryRef.current[key];
      if (entry && scrollDirRef.current === "down") {
        const { bottom } = entry.boundingClientRect;
        if (bottom < window.innerHeight * 0.25) return false;
      }
      return true;
    },
    [scrollDirRef, suppressBoundaryHide],
  );
  const isFullyOutOfView = useCallback((entry: IntersectionObserverEntry) => {
    const ratio = entry.intersectionRatio;
    return (
      ratio === 0 ||
      (!entry.isIntersecting && ratio <= SCROLL_BLOCK_EXIT_RATIO)
    );
  }, []);

  const initialAnimations = useMemo(
    () =>
      Object.fromEntries(keys.map((k) => [k, "hidden"])) as Record<
        K,
        ScrollBlockAnimState
      >,
    [keys],
  );

  const [animations, setAnimations] =
    useState<Record<K, ScrollBlockAnimState>>(initialAnimations);

  const [stableInView, setStableInView] = useState<Record<K, boolean>>(
    () => Object.fromEntries(keys.map((k) => [k, false])) as Record<K, boolean>,
  );

  const stableInViewRef = useRef(stableInView);
  stableInViewRef.current = stableInView;
  const inViewRefs = useRef(
    Object.fromEntries(keys.map((k) => [k, false])) as Record<K, boolean>,
  );
  const hideTimersRef = useRef<
    Partial<Record<K, ReturnType<typeof setTimeout>>>
  >({});

  const clearBlockHideTimer = useCallback((key: K) => {
    const timer = hideTimersRef.current[key];
    if (timer) {
      clearTimeout(timer);
      delete hideTimersRef.current[key];
    }
  }, []);

  const applyBlockVisible = useCallback(
    (key: K) => {
      if (!isEnabled(key)) return;
      clearBlockHideTimer(key);
      requestAnimationFrame(() => {
        setAnimations((prev) => {
          const cur = prev[key];
          if (scrollDirRef.current === "up") {
            // 表示済みを instant に差し替えると境目の上下スクロールで再アニメする
            if (cur !== "hidden") return prev;
            onBecameVisible?.(key, cur);
            return { ...prev, [key]: "instant" };
          }
          if (cur === "visible" || cur === "instant") {
            return prev;
          }
          onBecameVisible?.(key, cur);
          return { ...prev, [key]: "visible" };
        });
      });
    },
    [clearBlockHideTimer, isEnabled, onBecameVisible, scrollDirRef],
  );

  const scheduleBlockHide = useCallback(
    (key: K) => {
      if (!isEnabled(key)) return;
      clearBlockHideTimer(key);
      hideTimersRef.current[key] = setTimeout(() => {
        delete hideTimersRef.current[key];
        if (shouldSkipBoundaryHide(key)) return;
        if (!stableInViewRef.current[key]) {
          setAnimations((prev) =>
            prev[key] === "hidden" ? prev : { ...prev, [key]: "hidden" },
          );
        }
      }, SCROLL_BLOCK_HIDE_DEBOUNCE_MS);
    },
    [clearBlockHideTimer, isEnabled, shouldSkipBoundaryHide],
  );

  const notifyBlockIntersection = useCallback(
    (key: K, entry: IntersectionObserverEntry) => {
      if (!isEnabled(key)) return;
      const ratio = entry.intersectionRatio;
      lastRatioRef.current[key] = ratio;
      lastEntryRef.current[key] = entry;
      const prevStable = stableInViewRef.current[key];
      let nextStable = prevStable;
      // 上スクロール時は再表示を早めに（EXIT と同じ閾値）。下スクロール時は ENTER でチラつき防止。
      const enterRatio =
        scrollDirRef.current === "up"
          ? SCROLL_BLOCK_EXIT_RATIO
          : SCROLL_BLOCK_ENTER_RATIO;
      if (ratio >= enterRatio) {
        nextStable = true;
      } else if (ratio <= SCROLL_BLOCK_EXIT_RATIO) {
        nextStable = false;
      }

      if (nextStable === prevStable) {
        if (!prevStable && isFullyOutOfView(entry)) {
          scheduleBlockHide(key);
        }
        return;
      }

      stableInViewRef.current = {
        ...stableInViewRef.current,
        [key]: nextStable,
      };
      setStableInView((prev) =>
        prev[key] === nextStable ? prev : { ...prev, [key]: nextStable },
      );
      inViewRefs.current[key] = nextStable;

      if (nextStable) {
        applyBlockVisible(key);
      } else {
        scheduleBlockHide(key);
      }
    },
    [applyBlockVisible, isEnabled, isFullyOutOfView, scheduleBlockHide, scrollDirRef],
  );

  const resyncAll = useCallback(() => {
    const dir = scrollDirRef.current;
    for (const key of keys) {
      if (!isEnabled(key)) continue;
      if (inViewRefs.current[key]) {
        clearBlockHideTimer(key);
      } else {
        scheduleBlockHide(key);
      }
    }

    requestAnimationFrame(() => {
      setAnimations((prev) => {
        let next = prev;
        let changed = false;

        for (const key of keys) {
          if (!isEnabled(key) || !inViewRefs.current[key]) continue;
          const cur = prev[key];
          let target: ScrollBlockAnimState | null = null;
          if (dir === "up" && cur === "hidden") {
            target = "instant";
          } else if (dir === "down" && cur === "hidden") {
            target = "visible";
          }
          if (target && target !== cur) {
            if (!changed) {
              next = { ...prev };
              changed = true;
            }
            if (cur === "hidden") onBecameVisible?.(key, cur);
            next[key] = target;
          }
        }

        return changed ? next : prev;
      });
    });
  }, [
    clearBlockHideTimer,
    isEnabled,
    keys,
    onBecameVisible,
    scheduleBlockHide,
    scrollDirRef,
  ]);

  const blockOnChange = useCallback(
    (key: K) => (_inView: boolean, entry?: IntersectionObserverEntry) => {
      if (entry) notifyBlockIntersection(key, entry);
    },
    [notifyBlockIntersection],
  );

  useEffect(
    () => () => {
      for (const key of Object.keys(hideTimersRef.current) as K[]) {
        clearBlockHideTimer(key);
      }
    },
    [clearBlockHideTimer],
  );

  return {
    animations,
    stableInView,
    blockOnChange,
    resyncAll,
    inViewRefs,
    setAnimations,
  };
}
